import { useEffect, useRef } from "react";

/**
 * Rafter-tail pricing trial (model rafter_tail_linked_v1, Andy 2026-10-20).
 *
 * A line opts in by recording `pricing_model` — the backend prices it from
 * the rafter-tail costing rules and the linked tier ladder. A line without it
 * keeps the rate card, which is how saved quotes stay priced as they were.
 */

export const RAFTER_TAIL_TYPE_KEY = "rafter_tail";
export const RAFTER_TAIL_TRIAL_MODEL = "rafter_tail_linked_v1";

export const TRIAL_MODEL_LABEL = "Pricing trial (Oct 2026)";
export const RATE_CARD_MODEL_LABEL = "Rate card";

/** Ladder order: what Island charges each channel, cheapest first. */
export const LINKED_TIER_ORDER = [
  "distributor",
  "dealer",
  "contractor",
  "retail",
];

export const LINKED_TIER_LABELS = {
  distributor: "Distributor",
  dealer: "Dealer",
  contractor: "Contractor",
  retail: "Retail",
};

export const LINKED_TIER_RULES = {
  distributor: "Island's price — cost + overhead, profit, commission",
  dealer: "Distributor ÷ 0.80",
  contractor: "Retail less 20%",
  retail: "Dealer ÷ 0.60",
};

/** Paint is the only finish choice on the trial model: on or off. */
export const TRIAL_FINISH_OPTIONS = [
  { value: "prep_only", label: "No paint" },
  { value: "island_finish", label: "Paint / stain (2 coats)" },
];

export function isRafterTailTrialLine(lineItem) {
  return (
    Boolean(lineItem) &&
    lineItem.mode !== "custom" &&
    lineItem.pricing_model === RAFTER_TAIL_TRIAL_MODEL
  );
}

/** Whether the line can be put on (or taken off) the trial model. */
export function canUseRafterTailTrial(lineItem) {
  return (
    Boolean(lineItem) &&
    lineItem.mode !== "custom" &&
    lineItem.product_type_key === RAFTER_TAIL_TYPE_KEY
  );
}

export function trialFinishSelected(lineItem) {
  return lineItem?.finish_mode === "island_finish";
}

/**
 * The Distributor rates to keep on the quote, or null.
 *
 * Only rates the engine took from the live template are adopted, and only
 * while the quote has none: results priced from rates already saved (or
 * still on screen after "Reprice at today's rates") are never re-adopted.
 */
export function adoptableLinkedRates(results, currentRates) {
  if (currentRates) return null;
  const priced = (results?.line_item_results || []).find(
    (item) =>
      item?.linked_pricing?.distributor_rates_source === "pricing_settings",
  );
  return priced?.linked_pricing?.distributor_rates_used || null;
}

/**
 * The first time a quote prices a trial line from the live Distributor
 * template, keep those rates on the quote. Every later calculation and save
 * then uses them, so a template change never silently reprices a quote
 * already priced. "Reprice at today's rates" clears them.
 *
 * Only a calculation that finishes AFTER the rates were cleared may be
 * adopted: the results on screen at that moment can themselves say
 * "pricing_settings" (a reopened quote shows the run from its first save),
 * and adopting them would silently undo the reprice.
 */
export function useAdoptLinkedRates(results, linkedRates, dispatch) {
  const previousRates = useRef(linkedRates);
  const staleResults = useRef(null);
  useEffect(() => {
    if (previousRates.current && !linkedRates) {
      staleResults.current = results;
    }
    previousRates.current = linkedRates;
    if (results && results === staleResults.current) return;
    const rates = adoptableLinkedRates(results, linkedRates);
    if (rates) {
      dispatch({ type: "UPDATE_FIELD", field: "linked_rates", value: rates });
    }
  }, [results, linkedRates, dispatch]);
}
